import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useLoginMutation } from "../features/auth/authApi";

const Login = ({ setOpenLoginModal, setOpenRegisterModal, setOpenForgotPasswordModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [login] = useLoginMutation();

  const initialValues = {
    login: "",
    password: "",
  };

  const validationSchema = Yup.object({
    login: Yup.string().required("Մուտքանունը պարտադիր է"),
    password: Yup.string().required(t("loginComponent.errors.passwordRequired")),
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setMessage(null);

    try {
      const data = await login(values).unwrap();
      setMessage({
        status: true,
        message: data?.message || t("loginComponent.success"),
      });
      setOpenLoginModal(false);
      const locale = pathname.split("/").filter(Boolean)[0];
      navigate(locale === "en" ? "/en/profile" : "/profile");
    } catch (error) {
      const validationErrors = error?.data?.errors || error?.data?.data?.errors;
      if (validationErrors && typeof validationErrors === "object") {
        Object.entries(validationErrors).forEach(([field, errors]) => {
          setFieldError(field, Array.isArray(errors) ? errors[0] : errors);
        });
      }
      setMessage({
        status: false,
        message:
          error?.data?.message ||
          error?.data?.error ||
          t("loginComponent.errors.loginFailed"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      {/* ================================== Account Page Start =========================== */}
      <section className="account d-flex">
        <div className="account__right padding-y-120 flx-align">
          <div className="dark-light-mode">
            {/* Light Dark Mode */}
            {/* <ThemeToggle /> */}
          </div>
          <div className="account-content">
            <Link to="/" className="logo">
              <img
                src="assets/images/logo/logo.png"
                alt=""
                className="white-version"
              />
              <img
                src="assets/images/logo/white-logo.png"
                alt=""
                className="dark-version"
              />
            </Link>
            {/* <h4 className="account-content__title mb-48 text-capitalize">
              Welcome Back!
            </h4> */}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="row">
                  {message && (
                    <div
                      className={`col-12 ${
                        message.status ? "text-success" : "text-danger"
                      }`}
                    >
                      {message.message}
                    </div>
                  )}
                  <div className="col-12">
                    <label
                      htmlFor="login"
                      className="form-label font-18 font-heading fw-600"
                    >
                      Մուտքանուն
                    </label>
                    <div className="position-relative">
                      <Field
                        type="text"
                        name="login"
                        className="common-input common-input--bg common-input--withIcon"
                        id="login"
                        placeholder="Մուտքանուն"
                        autoComplete="username"
                      />
                      <span className="input-icon">
                        <img src="assets/images/icons/user-icon.svg" alt="" />
                      </span>
                    </div>
                    <ErrorMessage
                      name="login"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <label
                      htmlFor="password"
                      className="form-label font-18 font-heading fw-600"
                    >
                      {t("loginComponent.password")}
                    </label>
                    <div className="position-relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="common-input common-input--bg common-input--withIcon"
                        id="password"
                        placeholder={t("loginComponent.passwordPlaceholder")}
                      />
                      <button type="button" className="input-icon password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"}>
                        <i className={`las ${showPassword ? "la-eye-slash" : "la-eye"}`} />
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <div className="flx-between gap-1">
                      <div className="common-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="checkbox"
                          id="keepMe"
                        />
                        <label
                          className="form-check-label fw-400 font-14 text-body"
                          htmlFor="keepMe"
                        >
                          {t("loginComponent.keepSignedIn")}
                        </label>
                      </div>
                      <Link
                        to="#"
                        onClick={(event) => { event.preventDefault(); setOpenLoginModal(false); setOpenForgotPasswordModal(true); }}
                        className="forgot-password text-decoration-underline text-main text-poppins font-14"
                      >
                        {t("loginComponent.forgotPassword")}
                      </Link>
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-main btn-lg w-100 pill"
                    >
                      {" "}
                      {isSubmitting
                        ? t("loginComponent.signingIn")
                        : t("loginComponent.signIn")}
                    </button>
                  </div>
                  {/* <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-outline-light btn-lg-icon btn-lg w-100 pill"
                    >
                      <span className="icon icon-left">
                        <img src="assets/images/icons/google.svg" alt="" />
                      </span>
                      {t("loginComponent.signInWithGoogle")}
                    </button>
                  </div> */}
                  <div className="col-sm-12">
                    <div className="have-account">
                      <p className="text font-14">
                        {t("loginComponent.newToMarket")}{" "}
                        <span
                          className="link text-main text-decoration-underline fw-500"
                          onClick={() => {
                            setOpenLoginModal(false);
                            setOpenRegisterModal(true);
                          }}
                        >
                          {t("loginComponent.signUp")}
                        </span>
                      </p>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
      {/* ================================== Account Page End =========================== */}
    </>
  );
};

export default Login;
